import type { ProductOut, StatoOfferta } from "@/lib/api";

// Esito buybox per la UI, dalla classificazione del backend (services/stato_offerta.py):
//   "won"  -> "Buybox vinta"  (vinta)
//   "lost" -> "Buybox persa"  (recuperabile, al limite, persa per altro)
//   null   -> spenta, price-setter, nessuna offerta nostra, stato sconosciuto: "—"
export type BuyboxOutcome = "won" | "lost" | null;

const PERSE: ReadonlySet<StatoOfferta> = new Set<StatoOfferta>([
  "recuperabile",
  "al_limite",
  "persa_altro",
]);

export function buyboxOutcome(product: Pick<ProductOut, "stato_offerta">): BuyboxOutcome {
  if (product.stato_offerta === "vinta") return "won";
  if (product.stato_offerta !== null && PERSE.has(product.stato_offerta)) return "lost";
  return null;
}

/** Etichetta IT per l'esito buybox. `null` -> "—" (non vendiamo / non classificabile). */
export function buyboxOutcomeLabel(outcome: BuyboxOutcome): string {
  if (outcome === "won") return "Buybox vinta";
  if (outcome === "lost") return "Buybox persa";
  return "—";
}

// Canali PRICE-SETTER (BricoBravo): NON hanno competitor/buybox. Lo stato
// riflette il rispetto del margine obiettivo. Questi stati sono ESCLUSIVI dei
// canali price-setter (gli altri marketplace non li producono), quindi sono
// riconoscibili dal solo `buybox_state`, senza sapere il marketplace.
// Tipo derivato dall'array sorgente: union e Set non possono divergere (un
// nuovo stato si aggiunge in un solo punto).
const PRICE_SETTER_STATE_LIST = ["at_margin", "off_margin", "no_floor"] as const;
export type PriceSetterStatus = (typeof PRICE_SETTER_STATE_LIST)[number];

const PRICE_SETTER_STATES: ReadonlySet<string> = new Set(PRICE_SETTER_STATE_LIST);

function assertNever(x: never): never {
  throw new Error(`priceSetterLabel: stato price-setter non gestito "${x as string}"`);
}

/** Stato price-setter del prodotto, o `null` se il canale ha competitor (buybox). */
export function priceSetterStatus(
  product: Pick<ProductOut, "buybox_state">,
): PriceSetterStatus | null {
  const state = product.buybox_state ?? "";
  return PRICE_SETTER_STATES.has(state) ? (state as PriceSetterStatus) : null;
}

/** Etichetta IT per lo stato price-setter. */
export function priceSetterLabel(status: PriceSetterStatus): string {
  switch (status) {
    case "at_margin":
      return "A margine";
    case "off_margin":
      return "Da correggere";
    case "no_floor":
      return "No costo";
    default:
      return assertNever(status);
  }
}
